import { Location } from 'history';
import { Suspense } from 'react';
import { JSX } from 'react/jsx-runtime';
import { connect } from 'react-redux';
import { Persistor } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import { Route, Switch } from 'react-router';

// Types
import { RootState } from 'types/RootState.types';
import { SessionManagerProps } from '@views/login-session/SessionManager.types';
import { MainProps } from '@views/main/Main.types';

// Config
import routesConstants from '@constants/routeConstants';
import { WindowSizeProvider } from '@context/window/WindowSizeContext';
import { getRouterLocationSelector } from '@redux/reducers/router/routerSelector';
import {
  SinglePageSwitchLazy,
  OrderConfirmationLazy,
  internationalPhoneNumberLazy,
  MainLazy,
  Auth0CallbackGuestLazy,
  Auth0CallbackAuthenticatedLazy,
  Auth0CallbackOrderClaimLazy,
  Auth0CallbackCreateAccountLazy,
  AgeVerificationCallbackLazy,
} from '@utils/lazy/lazyMainBundle';

// Components
import App from './App';
import Login from '@views/login/Login';
import SessionManager from '@views/login-session/SessionManager';
import NotFound from '@views/not-found/NotFound';
import SessionExpired from '@views/session-expired';

const Fallback = () => <div />;
const getMultiPathPattern = (paths: string[]) => `(${paths.join('|')})`;

const AppRoute = connect((state: RootState) => ({
  location: getRouterLocationSelector(state),
}))(App);

const ConnectedSwitch = connect((state: RootState) => ({
  location: getRouterLocationSelector(state) as Location,
}))(Switch);

const MainManagerPaths: string[] = [
  routesConstants.DELIVERY,
  routesConstants.DELIVERY_ADDRESS_BOOK,
  routesConstants.DELIVERY_ADDRESS_BOOK_NEW,
  routesConstants.DELIVERY_ADDRESS_BOOK_EDIT,
  routesConstants.DELIVERY_OPTIONS,
  routesConstants.CLICK_AND_COLLECT,
  routesConstants.CLICK_AND_COLLECT_STORE_DETAILS,
  routesConstants.CLICK_AND_COLLECT_SAVED_COLLECTION_POINTS,
  routesConstants.CLICK_AND_COLLECT_STORE_SELECTED,
  routesConstants.CLICK_AND_COLLECT_SEARCH_MAP,
  routesConstants.CLICK_AND_COLLECT_SEARCH_LIST,
  routesConstants.PAYMENT,
  routesConstants.PAYMENT_STORE_DETAILS,
  routesConstants.PAYMENT_GIFT_OPTIONS,
  routesConstants.PAYMENT_ADDRESS_BOOK,
  routesConstants.PAYMENT_ADDRESS_BOOK_NEW,
  routesConstants.PAYMENT_ADDRESS_BOOK_EDIT,
  routesConstants.PAYMENT_POS_CREDIT_CALLBACK,
  routesConstants.ORDER_CONFIRMATION,
  routesConstants.ORDER_CONFIRMATION_STORE_DETAILS,
  routesConstants.ORDER_CONFIRMATION_JOIN_MY_JL,
  routesConstants.ORDER_CONFIRMATION_JOIN_MY_JL_NEW,
  routesConstants.ORDER_CONFIRMATION_JOIN_MY_JL_EDIT,
  routesConstants.EDIT_BASKET,
  routesConstants.SESSION_EXPIRED,
  routesConstants.CALLBACK_LOGIN_AUTHENTICATED,
  routesConstants.CALLBACK_LOGIN_GUEST,
  routesConstants.CALLBACK_ORDER_CLAIM,
  routesConstants.CALLBACK_CREATE_ACCOUNT,
  routesConstants.LEAVE_CHECKOUT,
  routesConstants.JOIN_MY_JL,
  routesConstants.JOIN_MY_JL_NEW,
  routesConstants.JOIN_MY_JL_EDIT,
  routesConstants.CALLBACK_AGE_VERIFICATION,
  routesConstants.CALLBACK_AGE_VERIFICATION_CANCEL,
];
const MainManagerPathsPattern = getMultiPathPattern(MainManagerPaths);

function routes(persist: Persistor) {
  // initiate preloading of our main bundle
  internationalPhoneNumberLazy();

  const LoginSessionManagerPaths = [
    routesConstants.LOGIN,
    routesConstants.APPS_GUEST_HANDOVER,
    routesConstants.APPS_AUTHENTICATED_HANDOVER,
    routesConstants.WEB_HANDOVER,
  ];

  const LoginSessionManagerPathsPattern = getMultiPathPattern(LoginSessionManagerPaths);

  const LoginSessionManager = (props: JSX.IntrinsicAttributes & SessionManagerProps) => (
    <SessionManager {...props}>
      <Switch>
        <Route exact path={routesConstants.LOGIN} render={() => <Login />} />
      </Switch>
    </SessionManager>
  );

  const SinglePageSwitch = () => (
    <Suspense fallback={<Fallback />}>
      <SinglePageSwitchLazy />
    </Suspense>
  );

  const OrderConfirmation = () => (
    <Suspense fallback={<Fallback />}>
      <OrderConfirmationLazy />
    </Suspense>
  );

  const Auth0CallbackGuest = () => (
    <Suspense fallback={<Fallback />}>
      <Auth0CallbackGuestLazy />
    </Suspense>
  );

  const Auth0CallbackAuthenticated = () => (
    <Suspense fallback={<Fallback />}>
      <Auth0CallbackAuthenticatedLazy />
    </Suspense>
  );

  const Auth0CallbackOrderClaim = () => (
    <Suspense fallback={<Fallback />}>
      <Auth0CallbackOrderClaimLazy />
    </Suspense>
  );

  const Auth0CallbackCreateAccount = () => (
    <Suspense fallback={<Fallback />}>
      <Auth0CallbackCreateAccountLazy />
    </Suspense>
  );

  const AgeVerificationCallback = () => (
    <Suspense fallback={<Fallback />}>
      <AgeVerificationCallbackLazy />
    </Suspense>
  );

  const MainManager = (props: JSX.IntrinsicAttributes & MainProps) => (
    <Suspense fallback={<p>Loading...</p>}>
      <MainLazy {...props}>
        <Switch>
          {/* DELIVERY */}
          <Route component={SinglePageSwitch} exact path={routesConstants.DELIVERY_ADDRESS_BOOK_EDIT} />
          <Route component={SinglePageSwitch} exact path={routesConstants.DELIVERY_ADDRESS_BOOK_NEW} />
          <Route component={SinglePageSwitch} exact path={routesConstants.DELIVERY_ADDRESS_BOOK} />
          <Route component={SinglePageSwitch} exact path={routesConstants.DELIVERY_OPTIONS} />
          <Route component={SinglePageSwitch} exact path={routesConstants.DELIVERY} />
          {/* CLICK AND COLLECT */}
          <Route component={SinglePageSwitch} exact path={routesConstants.CLICK_AND_COLLECT_SAVED_COLLECTION_POINTS} />
          <Route component={SinglePageSwitch} exact path={routesConstants.CLICK_AND_COLLECT_SEARCH_LIST} />
          <Route component={SinglePageSwitch} exact path={routesConstants.CLICK_AND_COLLECT_SEARCH_MAP} />
          <Route component={SinglePageSwitch} exact path={routesConstants.CLICK_AND_COLLECT_STORE_DETAILS} />
          <Route component={SinglePageSwitch} exact path={routesConstants.CLICK_AND_COLLECT_STORE_SELECTED} />
          <Route component={SinglePageSwitch} exact path={routesConstants.CLICK_AND_COLLECT} />
          {/* PAYMENT */}
          <Route component={SinglePageSwitch} exact path={routesConstants.PAYMENT_ADDRESS_BOOK_NEW} />
          <Route component={SinglePageSwitch} exact path={routesConstants.PAYMENT_ADDRESS_BOOK_EDIT} />
          <Route component={SinglePageSwitch} exact path={routesConstants.PAYMENT_ADDRESS_BOOK} />
          <Route component={SinglePageSwitch} exact path={routesConstants.PAYMENT_CARD_DELETE} />
          <Route component={SinglePageSwitch} exact path={routesConstants.PAYMENT_GIFT_OPTIONS} />
          <Route component={SinglePageSwitch} exact path={routesConstants.PAYMENT_GIFT_OPTIONS} />
          <Route component={SinglePageSwitch} exact path={routesConstants.PAYMENT_STORE_DETAILS} />
          <Route component={SinglePageSwitch} exact path={routesConstants.PAYMENT} />
          <Route component={SinglePageSwitch} exact path={routesConstants.PAYMENT_POS_CREDIT_CALLBACK} />
          {/* EDIT BASKET */}
          <Route component={SinglePageSwitch} exact path={routesConstants.EDIT_BASKET} />
          {/* ORDER CONFIRMATION */}
          <Route component={OrderConfirmation} exact path={routesConstants.ORDER_CONFIRMATION_JOIN_MY_JL_EDIT} />
          <Route component={OrderConfirmation} exact path={routesConstants.ORDER_CONFIRMATION_JOIN_MY_JL_NEW} />
          <Route component={OrderConfirmation} exact path={routesConstants.ORDER_CONFIRMATION_JOIN_MY_JL} />
          <Route component={OrderConfirmation} exact path={routesConstants.ORDER_CONFIRMATION_STORE_DETAILS} />
          <Route component={OrderConfirmation} exact path={routesConstants.ORDER_CONFIRMATION} />
          <Route component={OrderConfirmation} exact path={routesConstants.JOIN_MY_JL} />
          <Route component={OrderConfirmation} exact path={routesConstants.JOIN_MY_JL_EDIT} />
          <Route component={OrderConfirmation} exact path={routesConstants.JOIN_MY_JL_NEW} />
          {/* SESSION EXPIRED */}
          <Route component={SessionExpired} exact path={routesConstants.SESSION_EXPIRED} />
          <Route component={SinglePageSwitch} exact path={routesConstants.LEAVE_CHECKOUT} />
          {/* AUTH0 CALLBACKS */}
          <Route component={Auth0CallbackAuthenticated} exact path={routesConstants.CALLBACK_LOGIN_AUTHENTICATED} />
          <Route component={Auth0CallbackGuest} exact path={routesConstants.CALLBACK_LOGIN_GUEST} />
          <Route component={Auth0CallbackOrderClaim} exact path={routesConstants.CALLBACK_ORDER_CLAIM} />
          <Route component={Auth0CallbackCreateAccount} exact path={routesConstants.CALLBACK_CREATE_ACCOUNT} />
          <Route component={AgeVerificationCallback} exact path={routesConstants.CALLBACK_AGE_VERIFICATION} />
          <Route component={AgeVerificationCallback} exact path={routesConstants.CALLBACK_AGE_VERIFICATION_CANCEL} />
        </Switch>
      </MainLazy>
    </Suspense>
  );

  return (
    <WindowSizeProvider>
      <PersistGate loading={null} persistor={persist}>
        <AppRoute>
          <ConnectedSwitch data-variant="on">
            <Route
              exact
              path={LoginSessionManagerPathsPattern}
              render={(props) => <LoginSessionManager {...props} />}
            />

            {/* MAIN BUNDLE - LAZY LOADS WHEN ANY OF THESE PATHS MATCH */}
            <Route path={MainManagerPathsPattern} render={() => <MainManager />} />

            {/* FALLBACK - ALL UNMATCHED ROUTES */}
            <Route render={() => <NotFound />} />
          </ConnectedSwitch>
        </AppRoute>
      </PersistGate>
    </WindowSizeProvider>
  );
}

export default routes;
